import { useEffect } from "react";
import { Link, Outlet } from "react-router-dom";
import { WifiOff, CloudUpload, ShieldOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MobileProvider, useMobile } from "@/context/MobileContext";
import { WaAlerts } from "@/components/WaAlerts";
import { MDrawer } from "@/components/mobile/MDrawer";
import { HelpAssistant } from "@/components/help/HelpAssistant";
import { canSee } from "@/lib/api";
import { cn } from "@/lib/utils";
import "@/lib/install";

export function useForceLight() {
  useEffect(() => {
    const el = document.documentElement;
    const had = el.classList.contains("dark");
    el.classList.remove("dark");
    return () => { if (had) el.classList.add("dark"); };
  }, []);
}

const HEAD_TAGS = [["meta", { name: "theme-color", content: "#1565c0" }],
  ["meta", { name: "apple-mobile-web-app-capable", content: "yes" }], ["meta", { name: "mobile-web-app-capable", content: "yes" }],
  ["meta", { name: "apple-mobile-web-app-title", content: "Comandas" }], ["link", { rel: "apple-touch-icon", href: "/icons/icon-192.png" }]];

export function useInstallable(token = localStorage.getItem("m_link_token")) {
  const manifest = token ? `/api/public/mobile-link/${token}/manifest` : "/manifest-m.json";
  useEffect(() => {
    const els = [["link", { rel: "manifest", href: manifest }], ...HEAD_TAGS].map(([tag, attrs]) => {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      return el;
    });
    navigator.serviceWorker?.register("/sw.js").catch(() => {});
    return () => els.forEach((el) => el.remove());
  }, [manifest]);
}

function OfflineBar() {
  const { online, queue } = useMobile();
  const waiting = queue.filter((e) => !e.error).length;
  const failed = queue.filter((e) => e.error).length;
  if (online && !waiting && !failed) return null;
  return (
    <Link to="/m/backup" className={cn("flex items-center gap-2 px-4 py-2 text-xs font-semibold", online ? "bg-amber-100 text-amber-900" : "bg-red-600 text-white")} data-testid="m-offline-bar">
      {online ? <CloudUpload className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
      <span className="flex-1">{online ? "Conectado" : "Sem internet"}{waiting ? ` · ${waiting} envio(s) guardado(s) no celular` : ""}{failed ? ` · ${failed} com erro` : ""}</span>
      <span className="underline">Ver</span>
    </Link>
  );
}

function NoAccess() {
  return (
    <div className="grid min-h-[100dvh] place-items-center bg-slate-50 p-6 text-center" data-testid="m-no-access">
      <div className="space-y-3">
        <ShieldOff className="mx-auto h-10 w-10 text-slate-400" />
        <p className="font-display text-lg font-semibold">Seu usuário não tem acesso às comandas</p>
        <Link to="/app" className="inline-block rounded-xl bg-[#1565c0] px-5 py-3 text-sm font-semibold text-white" data-testid="m-open-full-btn">Abrir o sistema completo</Link>
      </div>
    </div>
  );
}

export default function MobileShell() {
  const { session } = useAuth();
  useForceLight();
  useInstallable();
  if (!canSee(session.user, "comandas")) return <NoAccess />;
  return (
    <MobileProvider>
      <WaAlerts />
      <HelpAssistant variant="mobile" />
      <div className="min-h-[100dvh] bg-slate-200/60 text-slate-900" data-testid="mobile-app">
        <div className="relative mx-auto min-h-[100dvh] max-w-md bg-slate-50 shadow-xl shadow-slate-300/40">
          <OfflineBar />
          <Outlet />
        </div>
        <MDrawer />
      </div>
    </MobileProvider>
  );
}
